// @react
import React, { Dispatch, ReactElement, SetStateAction, useRef } from 'react'
// @libraries
import {
  Control,
  Controller,
  FieldErrors,
  UseFormMethods,
} from 'react-hook-form'
import Helper from 'utils/Helper.class'
// @typings
import { ProfileFormValues } from 'modules/common/forms/types/profile'
import { GetUserQueryResult } from 'lib/graphql/graphqlTypes'
// @components
import Grid from '@bit/atd.web.grid'
import InputAvatar from '@bit/atd.web.forms.input-avatar'
import InputText from '@bit/atd.web.forms.input-text'
import InputTinyMCE from '@bit/atd.web.forms.input-tiny-mce'
import strings from 'lib/constants/strings'

const TINY_MCE_KEY: string = process.env.REACT_APP_TINY_MCE_KEY as string

type PropsType = {
  userData?: GetUserQueryResult['data']
  formMethods: UseFormMethods<ProfileFormValues>
  control: Control<ProfileFormValues>
  errors: FieldErrors
  setImageFile: Dispatch<SetStateAction<File[] | [] | undefined>>
  editing?: boolean
}

const AboutFields = ({
  userData,
  formMethods,
  control,
  errors,
  setImageFile,
  editing,
}: PropsType): ReactElement => {
  /****************************************
   * REFS
   *****************************************/

  const firstNameRef = useRef<HTMLInputElement | null>(null)
  const lastNameRef = useRef<HTMLInputElement | null>(null)

  /******************************************
   * RENDER
   ******************************************/

  return (
    <>
      {/* Profile Photo */}
      <Grid xs={12} item>
        <Controller
          name="profileImageUrl"
          control={control}
          defaultValue={userData?.user?.profile?.profileImageUrl}
          render={({ onBlur, value, name }) => (
            <InputAvatar
              name={name}
              readOnly={!editing}
              label="Profile Picture"
              accept="image/gif,image/jpeg,image/png,image/jpg"
              maxSize={3145728}
              src={value as string}
              onBlur={onBlur}
              onChange={(src, acceptedFiles) => {
                formMethods.setValue('profileImageUrl', src, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
                setImageFile(acceptedFiles)
              }}
              onDropRejected={(fileRejections) => {
                // ERROR: multiple files uploaded
                if (fileRejections && fileRejections.length > 1) {
                  formMethods.setError('profileImageUrl', {
                    type: 'manual',
                    message: 'More then one image was uploaded!',
                  })
                }
                fileRejections?.map((file: any): void => {
                  file.errors.map((error: any): void => {
                    switch (error.code) {
                      case 'file-invalid-type':
                        formMethods.setError('profileImageUrl', {
                          type: 'manual',
                          message: 'Uploaded file was not an image!',
                        })
                        break
                      case 'file-too-large':
                        formMethods.setError('profileImageUrl', {
                          type: 'manual',
                          message: `Uploaded image exceeds file size limit! ${Helper.bytesToMegaBytes(
                            file.file.size,
                          )}`,
                        })
                        break
                    }
                  })
                })
              }}
              onFileDialogCancel={onBlur}
              helperText={
                <>
                  <b>Recommend size:</b> 300x300
                  <br />
                  <b>File size limit:</b> 3MB
                </>
              }
              error={!!errors?.profileImageUrl}
              errorText={errors?.profileImageUrl?.message}
            />
          )}
        />
      </Grid>

      {/* First name and Last name */}
      <Grid xs={12} sm={6} item>
        {/* First name */}
        <Controller
          name="firstName"
          control={control}
          onFocus={() => firstNameRef?.current?.focus()}
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              name={name}
              required
              disabled={editing}
              readOnly={!editing}
              label="First Name"
              placeholder={strings.PROFILE.BIO.ENTER_FIRST_NAME}
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
              inputRef={firstNameRef}
              error={!!errors?.firstName}
              errorText={errors?.firstName?.message}
            />
          )}
        />
      </Grid>
      {/* Last name */}
      <Grid xs={12} sm={6} item>
        <Controller
          name="lastName"
          control={control}
          onFocus={() => lastNameRef?.current?.focus()}
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              name={name}
              required
              disabled={editing}
              readOnly={!editing}
              label="Last Name"
              placeholder={strings.PROFILE.BIO.ENTER_LAST_NAME}
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
              inputRef={lastNameRef}
              error={!!errors?.lastName}
              errorText={errors?.lastName?.message}
            />
          )}
        />
      </Grid>

      {/* Biography */}
      <Grid xs={12} item>
        <Controller
          control={control}
          name="bio"
          render={({ onChange, onBlur, value, name }) => (
            <InputTinyMCE
              name={name}
              disabled={!editing}
              readOnly={!editing}
              label="Biography"
              placeholder={strings.PROFILE.BIO.BIO_PROVIDE_DESCRIPTION}
              maxChar={1500}
              apiKey={TINY_MCE_KEY}
              onBlur={onBlur}
              onChange={onChange}
              initialValue={value as string}
              value={value as string}
            />
          )}
        />
      </Grid>
    </>
  )
}

export default AboutFields
